import React from 'react';

/** Volledige pakketconfiguratie per collectief. Totaalpakket: abonnement (bundel) +
 *  verzekeringsvoorwaarden + dekkingspercentage + dekkingstabel per grade + Allianz-polis +
 *  buiten-bundel tarieven. Kredietopinies: alleen Allianz-koppeling + losse kredietcheck-tarieven.
 *  Herbruikt in de aanmaak-wizard en het per-collectief instellingenscherm. */

export type KredietcheckRij = { regio: string; prijs_ex_btw: number | string };
export type IncassoStaffelRij = { hoofdsom_van: number | string; hoofdsom_tot: number | string | null; fee_ex_btw: number | string };
export type VerzekeringRij = { grade: number | string; premie_pct: number | string };
export type IncassoCommissieRij = { bedrag_van: number | string; bedrag_tot: number | string | null; commissie_pct: number | string };
export type GradeDekkingRij = { grade: number | string; max_dekking: number | string };

export type BundelConfig = {
  naam: string;
  premie_bedrag: number | string;
  service_bedrag: number | string;
  incasso_periode: string;
  verzekeren_inbegrepen: boolean;
  inbegrepen_opinies: number | string;
  inbegrepen_incassos: number | string;
  max_dekking_per_member: number | string;
  tarief_extra_opinie: number | string;
  tarief_extra_incasso: number | string;
};

export type PakketConfig = {
  pakket_type: 'totaal' | 'kredietopinies';
  polisnummer: string;
  euler_hermes_id: string;
  voorwaarden: {
    max_factuur_leeftijd_dagen: number | string;
    max_betalingstermijn_dagen: number | string;
    overdracht_binnen_dagen: number | string;
    overdracht_uiterlijk_dagen: number | string;
  };
  dekkingspercentage: number | string;
  incasso_minimum_fee: number | string;
  bundel: BundelConfig;
  grade_coverage: GradeDekkingRij[];
  pricing: {
    kredietcheck: KredietcheckRij[];
    incasso: IncassoStaffelRij[];
    verzekering: VerzekeringRij[];
    incasso_commissie: IncassoCommissieRij[];
  };
};

export const LEGE_CONFIG = (pakket_type: 'totaal' | 'kredietopinies'): PakketConfig => ({
  pakket_type,
  polisnummer: '',
  euler_hermes_id: '',
  voorwaarden: { max_factuur_leeftijd_dagen: 90, max_betalingstermijn_dagen: 60, overdracht_binnen_dagen: 15, overdracht_uiterlijk_dagen: 60 },
  dekkingspercentage: 85,
  incasso_minimum_fee: 0,
  bundel: {
    naam: pakket_type === 'totaal' ? 'Pakket' : 'Kredietopinies',
    premie_bedrag: pakket_type === 'totaal' ? 0 : 0,
    service_bedrag: 0, incasso_periode: 'jaar',
    verzekeren_inbegrepen: pakket_type === 'totaal',
    inbegrepen_opinies: 10, inbegrepen_incassos: 2, max_dekking_per_member: 50000,
    tarief_extra_opinie: 0, tarief_extra_incasso: 0,
  },
  grade_coverage: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((g) => ({ grade: g, max_dekking: ({ 1: 50000, 2: 50000, 3: 50000, 4: 25000, 5: 10000, 6: 5000 } as any)[g] || 0 })),
  pricing: {
    kredietcheck: [{ regio: 'NL', prijs_ex_btw: 15 }, { regio: 'EXPORT', prijs_ex_btw: 25 }],
    incasso: [{ hoofdsom_van: 0, hoofdsom_tot: 2500, fee_ex_btw: 75 }, { hoofdsom_van: 2500, hoofdsom_tot: null, fee_ex_btw: 125 }],
    verzekering: [1, 2, 3, 4, 5, 6].map((g) => ({ grade: g, premie_pct: ({ 1: 0.4, 2: 0.5, 3: 0.7, 4: 1.0, 5: 1.5, 6: 2.5 } as any)[g] })),
    incasso_commissie: [{ bedrag_van: 0, bedrag_tot: null, commissie_pct: 10 }],
  },
});

const num = (v: any): number => (v === '' || v == null ? 0 : Number(v));
const numOrNull = (v: any): number | null => (v === '' || v == null ? null : Number(v));

/** Zet de editor-state om naar de payload voor POST/PUT collectief-config. */
export function configNaarPayload(c: PakketConfig): any {
  const totaal = c.pakket_type === 'totaal';
  const pricing: any = {
    kredietcheck: c.pricing.kredietcheck.map((r) => ({ regio: r.regio, prijs_ex_btw: num(r.prijs_ex_btw) })),
  };
  if (totaal) {
    pricing.incasso = c.pricing.incasso.map((r) => ({ hoofdsom_van: num(r.hoofdsom_van), hoofdsom_tot: numOrNull(r.hoofdsom_tot), fee_ex_btw: num(r.fee_ex_btw) }));
    pricing.incasso_commissie = c.pricing.incasso_commissie.map((r) => ({ bedrag_van: num(r.bedrag_van), bedrag_tot: numOrNull(r.bedrag_tot), commissie_pct: num(r.commissie_pct) }));
    pricing.verzekering = c.pricing.verzekering.map((r) => ({ grade: num(r.grade), premie_pct: num(r.premie_pct) }));
  }
  const out: any = {
    polisnummer: c.polisnummer || '',
    euler_hermes_id: c.euler_hermes_id || '',
    pricing,
  };
  if (totaal) {
    out.max_factuur_leeftijd_dagen = num(c.voorwaarden.max_factuur_leeftijd_dagen);
    out.max_betalingstermijn_dagen = num(c.voorwaarden.max_betalingstermijn_dagen);
    out.overdracht_binnen_dagen = num(c.voorwaarden.overdracht_binnen_dagen);
    out.overdracht_uiterlijk_dagen = num(c.voorwaarden.overdracht_uiterlijk_dagen);
    out.dekkingspercentage = num(c.dekkingspercentage);
    out.incasso_minimum_fee = num(c.incasso_minimum_fee);
    out.grade_coverage = c.grade_coverage.map((r) => ({ grade: num(r.grade), max_dekking: num(r.max_dekking) }));
    out.bundel = {
      naam: c.bundel.naam || 'Pakket',
      premie_bedrag: num(c.bundel.premie_bedrag), service_bedrag: num(c.bundel.service_bedrag),
      incasso_periode: c.bundel.incasso_periode || 'jaar',
      verzekeren_inbegrepen: !!c.bundel.verzekeren_inbegrepen,
      inbegrepen_opinies: num(c.bundel.inbegrepen_opinies), inbegrepen_incassos: num(c.bundel.inbegrepen_incassos),
      max_dekking_per_member: num(c.bundel.max_dekking_per_member),
      tarief_extra_opinie: num(c.bundel.tarief_extra_opinie), tarief_extra_incasso: num(c.bundel.tarief_extra_incasso),
    };
  }
  return out;
}

const inp = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-1 focus:ring-mfb-blue outline-none';
const lbl = 'block text-xs font-medium text-slate-600 mb-1';

const Num: React.FC<{ value: any; onChange: (v: string) => void; step?: string }> = ({ value, onChange, step }) => (
  <input type="number" step={step || '0.01'} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inp} />
);
const Txt: React.FC<{ value: any; onChange: (v: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => (
  <input type="text" value={value ?? ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inp} />
);

const Sectie: React.FC<{ titel: string; sub?: string; children: React.ReactNode }> = ({ titel, sub, children }) => (
  <section>
    <h4 className="text-sm font-semibold text-mfb-blue mb-1">{titel}</h4>
    {sub && <p className="text-xs text-slate-500 mb-3">{sub}</p>}
    {children}
  </section>
);

export const PakketConfigEditor: React.FC<{ config: PakketConfig; onChange: (c: PakketConfig) => void }> = ({ config, onChange }) => {
  const totaal = config.pakket_type === 'totaal';
  const set = (patch: Partial<PakketConfig>) => onChange({ ...config, ...patch });
  const setV = (k: keyof PakketConfig['voorwaarden'], v: string) => set({ voorwaarden: { ...config.voorwaarden, [k]: v } });
  const setB = (k: keyof BundelConfig, v: any) => set({ bundel: { ...config.bundel, [k]: v } });
  const setP = (patch: Partial<PakketConfig['pricing']>) => set({ pricing: { ...config.pricing, ...patch } });

  return (
    <div className="space-y-7">
      {/* Allianz-koppeling */}
      <Sectie titel="Allianz Trade-koppeling" sub="Polis-/contractnummer en EulerHermes-ID van dit collectief.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
          {totaal && <div><label className={lbl}>Polisnummer (Allianz-contract)</label><Txt value={config.polisnummer} onChange={(v) => set({ polisnummer: v })} placeholder="bijv. POL-12345" /></div>}
          <div><label className={lbl}>EulerHermes-ID (collectief)</label><Txt value={config.euler_hermes_id} onChange={(v) => set({ euler_hermes_id: v })} /></div>
        </div>
      </Sectie>

      {totaal && (
        <Sectie titel="Abonnement (in de bundel, per lid)" sub="Vaste premie + servicefee per lid per periode, met inbegrepen aantallen en bijbetalingstarieven.">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className={lbl}>Naam pakket</label><Txt value={config.bundel.naam} onChange={(v) => setB('naam', v)} /></div>
            <div><label className={lbl}>Premie p/jr (€)</label><Num value={config.bundel.premie_bedrag} onChange={(v) => setB('premie_bedrag', v)} /></div>
            <div><label className={lbl}>Servicefee p/jr (€)</label><Num value={config.bundel.service_bedrag} onChange={(v) => setB('service_bedrag', v)} /></div>
            <div>
              <label className={lbl}>Incassoperiode</label>
              <select value={config.bundel.incasso_periode} onChange={(e) => setB('incasso_periode', e.target.value)} className={inp + ' bg-white'}>
                <option value="jaar">Jaar</option><option value="kwartaal">Kwartaal</option><option value="maand">Maand</option>
              </select>
            </div>
            <div><label className={lbl}>Opinies inbegrepen</label><Num value={config.bundel.inbegrepen_opinies} onChange={(v) => setB('inbegrepen_opinies', v)} step="1" /></div>
            <div><label className={lbl}>Incasso's inbegrepen</label><Num value={config.bundel.inbegrepen_incassos} onChange={(v) => setB('inbegrepen_incassos', v)} step="1" /></div>
            <div><label className={lbl}>Max dekking/lid (€)</label><Num value={config.bundel.max_dekking_per_member} onChange={(v) => setB('max_dekking_per_member', v)} /></div>
            <div><label className={lbl}>Extra opinie (€)</label><Num value={config.bundel.tarief_extra_opinie} onChange={(v) => setB('tarief_extra_opinie', v)} /></div>
            <div><label className={lbl}>Extra incasso (€)</label><Num value={config.bundel.tarief_extra_incasso} onChange={(v) => setB('tarief_extra_incasso', v)} /></div>
          </div>
        </Sectie>
      )}

      {totaal && (
        <Sectie titel="Verzekeringsvoorwaarden" sub="Bepalen wanneer een factuur verzekerbaar is en wanneer de dekking buiten termijn wordt afgemeld.">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className={lbl}>Max. factuurleeftijd (dagen)</label><Num value={config.voorwaarden.max_factuur_leeftijd_dagen} onChange={(v) => setV('max_factuur_leeftijd_dagen', v)} step="1" /></div>
            <div><label className={lbl}>Max. betalingsconditie (dagen)</label><Num value={config.voorwaarden.max_betalingstermijn_dagen} onChange={(v) => setV('max_betalingstermijn_dagen', v)} step="1" /></div>
            <div><label className={lbl}>Overdracht binnen (dagen)</label><Num value={config.voorwaarden.overdracht_binnen_dagen} onChange={(v) => setV('overdracht_binnen_dagen', v)} step="1" /></div>
            <div><label className={lbl}>Achterstalligheid — overdracht uiterlijk na vervaldatum (dagen)</label><Num value={config.voorwaarden.overdracht_uiterlijk_dagen} onChange={(v) => setV('overdracht_uiterlijk_dagen', v)} step="1" /></div>
            <div><label className={lbl}>Dekkingspercentage bij schade (%)</label><Num value={config.dekkingspercentage} onChange={(v) => set({ dekkingspercentage: v })} /></div>
          </div>
        </Sectie>
      )}

      {totaal && (
        <Sectie titel="Dekkingstabel per grade" sub="Maximaal verzekerd bedrag per Allianz-grade (1–10). 0 = niet verzekerbaar.">
          <StaffelTabel
            kolommen={[['grade', 'Grade'], ['max_dekking', 'Max. dekking (€)']]}
            rijen={config.grade_coverage}
            nieuweRij={{ grade: 1, max_dekking: 0 }}
            onChange={(rows) => set({ grade_coverage: rows as GradeDekkingRij[] })}
          />
        </Sectie>
      )}

      <Sectie titel="Buiten de bundel — tarieven" sub={totaal
        ? 'Tarieven voor losse afname (excl. btw).'
        : 'Kosten per losse kredietcheck (excl. btw) — dit pakket rekent per afname af.'}>
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Losse kredietcheck (per regio)</div>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {config.pricing.kredietcheck.map((r, i) => (
              <div key={r.regio}>
                <label className={lbl}>{r.regio === 'EXPORT' ? 'Export' : 'Nederland'} (€)</label>
                <Num value={r.prijs_ex_btw} onChange={(v) => { const a = [...config.pricing.kredietcheck]; a[i] = { ...r, prijs_ex_btw: v }; setP({ kredietcheck: a }); }} />
              </div>
            ))}
          </div>
        </div>

        {totaal && (
          <>
            <StaffelTabel titel="Incasso buiten de bundel (staffel op hoofdsom)"
              kolommen={[['hoofdsom_van', 'Hoofdsom vanaf (€)'], ['hoofdsom_tot', 'Tot (€, leeg = open)'], ['fee_ex_btw', 'Fee (€)']]}
              rijen={config.pricing.incasso} nieuweRij={{ hoofdsom_van: 0, hoofdsom_tot: null, fee_ex_btw: 0 }}
              onChange={(rows) => setP({ incasso: rows as IncassoStaffelRij[] })} openEinde="hoofdsom_tot" />

            <div className="mb-2">
              <label className={lbl}>Minimum incassofee (€)</label>
              <div className="max-w-[160px]"><Num value={config.incasso_minimum_fee} onChange={(v) => set({ incasso_minimum_fee: v })} /></div>
            </div>
            <StaffelTabel titel="Incasso-commissie (staffel op geïncasseerd bedrag, %)"
              kolommen={[['bedrag_van', 'Bedrag vanaf (€)'], ['bedrag_tot', 'Tot (€, leeg = open)'], ['commissie_pct', 'Commissie (%)']]}
              rijen={config.pricing.incasso_commissie} nieuweRij={{ bedrag_van: 0, bedrag_tot: null, commissie_pct: 0 }}
              onChange={(rows) => setP({ incasso_commissie: rows as IncassoCommissieRij[] })} openEinde="bedrag_tot" />

            <StaffelTabel titel="Losse verzekeringspremie (% per grade)"
              kolommen={[['grade', 'Grade'], ['premie_pct', 'Premie (%)']]}
              rijen={config.pricing.verzekering} nieuweRij={{ grade: 1, premie_pct: 0 }}
              onChange={(rows) => setP({ verzekering: rows as VerzekeringRij[] })} />
          </>
        )}
      </Sectie>
    </div>
  );
};

/** Generieke bewerkbare staffel/tabel met rij toevoegen/verwijderen. */
const StaffelTabel: React.FC<{
  titel?: string;
  kolommen: [string, string][];
  rijen: any[];
  nieuweRij: any;
  onChange: (rows: any[]) => void;
  openEinde?: string;
}> = ({ titel, kolommen, rijen, nieuweRij, onChange, openEinde }) => {
  const upd = (i: number, k: string, v: string) => {
    const a = [...rijen];
    a[i] = { ...a[i], [k]: (openEinde === k && v === '') ? null : v };
    onChange(a);
  };
  return (
    <div className="mb-4">
      {titel && <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{titel}</div>}
      <div className="overflow-x-auto">
        <table className="text-sm">
          <thead><tr>{kolommen.map(([, label]) => <th key={label} className="text-left text-xs text-slate-500 font-medium pr-2 pb-1">{label}</th>)}<th /></tr></thead>
          <tbody>
            {rijen.map((r, i) => (
              <tr key={i}>
                {kolommen.map(([k]) => (
                  <td key={k} className="pr-2 py-0.5">
                    <input type="number" step="0.001" value={r[k] ?? ''} placeholder={openEinde === k ? '∞' : ''}
                      onChange={(e) => upd(i, k, e.target.value)} className="w-28 px-2 py-1 border border-slate-300 rounded text-sm" />
                  </td>
                ))}
                <td><button type="button" onClick={() => onChange(rijen.filter((_, j) => j !== i))} className="text-xs text-slate-400 hover:text-red-600 px-1">✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={() => onChange([...rijen, { ...nieuweRij }])} className="mt-1 text-xs font-medium text-mfb-blue hover:underline">+ Regel toevoegen</button>
    </div>
  );
};

export default PakketConfigEditor;
